import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { TextField } from '@mui/material'
import { Button } from '@resiliatech/birdsong'
import CREATE_CHARTS from 'Apollo/Mutations/CreateCharts'
import { CalculationType, Chart } from 'Generated/graphql-types'
import { ChartCreationContainer } from './ChartCreation.styled'
import { Row, Text, TextFieldContainer } from './FunderSharedGoals.styled'

type ChartCreationProps = {
  popError: (message: string) => void
  popSuccess: (message: string) => void
}

export const ChartCreation = ({ popError, popSuccess }: ChartCreationProps) => {
  const [chartTitle, setChartTitle] = useState('')
  const [chartDescription, setChartDescription] = useState('')
  const [chartCategoriesString, setChartCategoriesString] = useState('')
  const [target, setTarget] = useState<string | number>('')
  const [goalIdsString, setGoalIdsString] = useState('')

  const [createChartMutation] = useMutation(CREATE_CHARTS)

  const isCreateChartButtonDisabled =
    !chartTitle || !chartDescription || !goalIdsString

  const areChartDetailsValid = () => {
    const ids = goalIdsString.split(',').map((id) => parseInt(id.trim(), 10))
    const categories = chartCategoriesString
      .split(',')
      .map((category) => category.trim())
    return (
      !!chartTitle &&
      !!chartDescription &&
      !!chartCategoriesString &&
      ids.every((id) => !Number.isNaN(id)) &&
      categories.every((category) => !!category)
    )
  }

  const handleCreateChart = async () => {
    const isValid = areChartDetailsValid()
    if (!isValid) {
      popError('Chart details invalid. Double-check inputs.')
      return
    }

    try {
      const goalIds = goalIdsString
        .split(',')
        .map((id) => parseInt(id.trim(), 10))

      const chartCategories = chartCategoriesString
        .split(',')
        .map((category) => ({
          title: category.trim(),
        }))

      const { data } = await createChartMutation({
        variables: {
          data: {
            calculationType: CalculationType.Cumulative,
            chartCategories,
            goalIds,
            target: target ? parseInt(target as string, 10) : undefined,
            title: chartTitle,
            unitOfMeasurement: chartDescription,
          },
        },
      })

      const chartsCreated = data?.createCharts

      if (chartsCreated.length === goalIds.length) {
        const successMessage = `${chartsCreated.length} charts created successfully!`
        popSuccess(successMessage)
        console.log(successMessage)
        handleResetChartDetails()
      } else {
        const failedGoalIds = goalIds
          .filter(
            (id) => !chartsCreated.some((chart: Chart) => chart.goalId === id)
          )
          .join(', ')
        popError(
          `Failed to create all charts. Partial chart-creation may have occurred. Failed goal IDs: ${failedGoalIds}`
        )
      }
    } catch (e) {
      popError('Failed to create chart. Please try again.')
    }
  }

  const handleResetChartDetails = () => {
    setChartTitle('')
    setChartDescription('')
    setChartCategoriesString('')
    setTarget('')
    setGoalIdsString('')
  }

  return (
    <ChartCreationContainer>
      <Text>
        Charts can be added using a list of goal IDs. Only a single category is
        currently supported for bulk chart creation
      </Text>
      <Text>
        {' '}
        (e.g. the thing you are measuring, such as &quot;Animals Saved&quot;).
      </Text>
      <Row>
        <Text>Chart Title:</Text>
        <TextFieldContainer>
          <TextField
            required
            label='Chart Title'
            value={chartTitle}
            sx={{ minWidth: '700px' }}
            onChange={({ target: { value } }) => setChartTitle(value)}
          />
        </TextFieldContainer>
      </Row>
      <Row>
        <Text>Chart Description:</Text>
        <TextFieldContainer>
          <TextField
            label='Chart Description'
            value={chartDescription}
            required
            sx={{ minWidth: '700px' }}
            onChange={({ target: { value } }) => setChartDescription(value)}
          />
        </TextFieldContainer>
      </Row>
      <Row>
        <Text>Chart Category:</Text>
        <TextFieldContainer>
          <TextField
            label='Chart Categories (e.g. Apples, Oranges)'
            value={chartCategoriesString}
            required
            sx={{ minWidth: '700px' }}
            onChange={({ target: { value } }) =>
              setChartCategoriesString(value)
            }
          />
        </TextFieldContainer>
      </Row>
      <Row>
        <Text>Target:</Text>
        <TextFieldContainer>
          <TextField
            label='Target (optional)'
            value={target}
            sx={{ minWidth: '700px' }}
            onChange={({ target: { value } }) => {
              setTarget(
                value && !Number.isNaN(parseInt(value, 10))
                  ? parseInt(value, 10)
                  : ''
              )
            }}
          />
        </TextFieldContainer>
      </Row>
      <Row>
        <Text>Goal IDs:</Text>
        <TextFieldContainer>
          <TextField
            label='Goal IDs (e.g. 1, 42, 100)'
            value={goalIdsString}
            sx={{ minWidth: '700px' }}
            required
            onChange={({ target: { value } }) => setGoalIdsString(value)}
          />
        </TextFieldContainer>
      </Row>
      <Row>
        <Button
          isDisabled={isCreateChartButtonDisabled}
          label='Add Chart to Goals'
          onClick={handleCreateChart}
          type='button'
          variant='secondary'
        />
        <Button
          label='Reset Chart Details'
          onClick={handleResetChartDetails}
          type='button'
          variant='secondary'
        />
      </Row>
    </ChartCreationContainer>
  )
}
